import { reactive } from 'vue';
import { fetchStorefront, clean } from '@utils/GraphQLClient';

export const VARIANT_DATA = `fragment variantData on ProductVariant {
  id
  sku
  image {
    altText
    url(transform: {maxWidth: 600})
  }
  compareAtPrice {
    amount
  }
  price {
    amount
  }
  selectedOptions {
    value
    name
  }
  product { id }
  textBadge: metafield(namespace: "acf_pdp", key: "variant_badge_messaging") { value }
  hoverImage: metafield(key: "hover_lifestyle_image", namespace: "acf_pdp") {
    reference {
      ... on MediaImage {
        image {
          url(
            transform: { maxWidth: 600 }
          )
        }
      }
    }
  }
}`;

export const PRODUCT_QUERY = `query ProductCard($handle: String!) {
  product(handle: $handle) {
    title
    id
    onlineStoreUrl
    priceRange{
      minVariantPrice {
        amount
      }
    }
    options(first: 3) {
      name
      values
    }
    variants(first: 1) {
      edges {
        node {
          ...variantData
        }
      }
    }
    tags
    shortDescription: metafield(namespace: "acf_pdp", key: "description_short") {
      value
    }
    fromPrice: metafield(namespace: "acf_pdp", key: "from_price") {
      value
    }
    textBadge: metafield(namespace: "acf_pdp", key: "badge_text_2") {
      value
    }
    imageBadge: metafield(namespace: "acf_pdp", key: "badge_asset") {
      value
    }
  }
}${VARIANT_DATA}`;

/**
 * Parses a raw Storefront API badge metafield value (JSON array string like '["Sale"]')
 * into a plain string. Returns empty string if the value is absent or falsy.
 * @param {string|null} raw - The raw metafield value from the Storefront API
 * @returns {string} The parsed badge string, or empty string
 */
const parseBadgeValue = (raw) => {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    const value = Array.isArray(parsed) ? parsed[0] : parsed;
    return value && value !== 'false' ? value : '';
  } catch {
    return raw !== 'false' ? raw : '';
  }
};

/**
 * Resolves the final badge text, preferring the Liquid-sourced value (which may
 * include collection-level overrides) and falling back to the Storefront API
 * product-level badge when Liquid cannot provide it (e.g. all_products limit).
 * @param {string} liquidBadge - Badge text sourced from Liquid
 * @param {string|null} apiBadgeRaw - Raw textBadge.value from Storefront API
 * @returns {string} The resolved badge text
 */
const resolveBadgeText = (liquidBadge, apiBadgeRaw) => {
  const liquid = (liquidBadge ?? '').trim();
  if (liquid && liquid !== 'false') return liquid;
  return parseBadgeValue(apiBadgeRaw);
};

/**
 * Resolves the final image badge URL. The Storefront API returns imageBadge.value
 * as a JSON array of objects (e.g. '[{"src":"https://...","width":...}]'), so we
 * must extract the .src property — unlike text badges which are plain strings.
 * @param {string} liquidAsset - Badge image URL sourced from Liquid
 * @param {string|null} apiBadgeRaw - Raw imageBadge.value from Storefront API
 * @returns {string} The resolved image URL, or empty string
 */
const resolveImageBadge = (liquidAsset, apiBadgeRaw) => {
  const liquid = (liquidAsset ?? '').trim();
  if (liquid && liquid !== 'false') return liquid;
  if (!apiBadgeRaw) return '';
  try {
    const parsed = JSON.parse(apiBadgeRaw);
    return (Array.isArray(parsed) ? parsed[0] : parsed)?.src ?? '';
  } catch {
    return '';
  }
};

const data = async (handle) => {
  return {
    variables: {
      handle: handle,
    },
    query: PRODUCT_QUERY,
  };
};

export const Options = {
  fixedWidth: '241px',
  gap: '1rem',
  lazyLoad: false,
  pagination: false,
  mediaQuery: 'min',
  perMove: 1,
  easing: 'cubic-bezier(.33, 1, .68, 1)',
  arrows: true,
  classes: {
    arrows: 'splide__arrows product-slider-arrows',
    arrow: 'splide__arrow product-slider-arrow',
    prev: 'splide__arrow--prev prev-arrow',
    next: 'splide__arrow--next next-arrow',
  },
  breakpoints: {
    433: {
      gap: '1.5rem',
      fixedWidth: '348px',
    },
    769: {
      gap: '2.5rem',
      fixedWidth: '452px',
    },
    1025: {
      gap: '2.5rem',
      fixedWidth: '411px',
    },
  },
};

export const FetchCards = async (cards) => {
  const items = reactive([]);
  for await (const card of cards) {
    const type = card?.cardType || 'PRODUCT';
    if (type === 'CONTENT') {
      const item = card;
      items.push(item);
    } else {
      const handle = card.handle ?? '';
      const sku = card.sku ?? '';
      const badgeText = card.badgeText ?? '';
      const badgeAsset = card.badgeAsset ?? '';
      const bundleMessaging = card.bundleMessaging ?? '';
      const customTitle = card.custom?.title ?? '';
      const customDesc = card.custom?.description ?? '';
      const dySelector = card.dySelector ?? '';
      const request = await data(handle);
      await fetchStorefront(request)
        .then((response) => {
          if (!response.data.product) return;

          const item = JSON.parse(JSON.stringify(clean(response.data.product)));
          item.cardType = 'PRODUCT';
          item.swatches =
            item.options
              .filter((option) => option.name === 'Color')
              .map((option) => option.values)[0] || [];
          item.featuredSku = sku;
          item.handle = handle;
          item.badgeText = resolveBadgeText(badgeText, item.textBadge?.value);
          item.badgeAsset = resolveImageBadge(
            badgeAsset,
            item.imageBadge?.value
          );
          item.bundleMessaging = bundleMessaging;
          item.customTitle = customTitle;
          item.customDesc = customDesc;
          item.edit = false;
          item.dySelector = dySelector;
          items.push(item);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  }

  return { items };
};

export const FetchCard = async (obj) => {
  try {
    const type = obj?.cardType || 'PRODUCT';
    if (type === 'CONTENT') return false;

    const handle = obj.handle ?? '';
    const sku = obj.sku ?? '';
    const badgeText = obj.badgeText ?? '';
    const badgeAsset = obj.badgeAsset ?? '';
    const customTitle = obj.custom?.title ?? '';
    const customDesc = obj.custom?.description ?? '';
    const request = await data(handle);
    const response = await fetchStorefront(request).then((res) =>
      clean(res.data.product)
    );
    const item = JSON.parse(JSON.stringify(response));
    item.cardType = 'PRODUCT';
    item.swatches =
      item.options
        .filter((option) => option.name === 'Color')
        .map((option) => option.values)[0] || [];
    item.featuredSku = sku;
    item.handle = handle;

    item.badgeText = resolveBadgeText(badgeText, item.textBadge?.value);
    item.badgeAsset = resolveImageBadge(badgeAsset, item.imageBadge?.value);
    item.customTitle = customTitle;
    item.customDesc = customDesc;
    item.edit = false;
    item.index = obj.index ?? false;

    if (obj.variant) {
      const variant = item.variants[0];
      variant.id = obj.variant.id;
      variant.image = {
        altText: obj.variant.featured_image.alt,
        url: obj.variant.featured_image.src,
      };
    }

    return { item };
  } catch (err) {
    return { err };
  }
};

export const fetchSwatch = (color) => {
  const swatches = window?.Brooklinen?.product?.swatches || false;

  const matchingSwatch = swatches.filter((swatch) => swatch.name === color);

  if (matchingSwatch.length <= 0) return false;

  const swatch = matchingSwatch[0].value;
  const isHex = swatch[0] === '#';

  const style = isHex
    ? `background: ${swatch}`
    : `background-image: url(${swatch})`;

  return style;
};
